import type { CombinedError } from '@urql/core';

const getValidationErrorMessages = (validation: Record<string, string[][]>) => {
  const validationErrorKeys = Object.keys(validation);
  if (!validationErrorKeys[0]) {
    return null;
  }

  const validationErrors = validation[validationErrorKeys[0]];

  if (!validationErrors) {
    return null;
  }

  return validationErrors[0];
};

const getErrorMessagesFromCombinedErrors = (errorObject: CombinedError): string[] => {
  return errorObject.graphQLErrors
    .map((e) => {
      if (e.extensions.code) {
        return <string>e.extensions.clientMessage;
      }

      if (e.extensions.validation) {
        return getValidationErrorMessages(<Record<string, string[][]>>e.extensions.validation);
      }

      return null;
    })
    .filter((e): e is string => e !== null);
};

export const getFirstErrorMessageFromCombinedErrors = (
  t: (key: string) => string,
  errorObject: CombinedError,
  defaultErrorMessage?: string,
) => {
  const errorMessages = getErrorMessagesFromCombinedErrors(errorObject);
  if (errorMessages && errorMessages[0]) {
    return errorMessages[0];
  }

  return defaultErrorMessage || t('general.unexpectedError');
};

/**
 * Resolve a display message from a caught error that may be a CombinedError OR a plain Error
 * carrying an already-translated message (the shape composables like saveArticle throw).
 * getFirstErrorMessageFromCombinedErrors crashes on anything without `graphQLErrors`, so only
 * CombinedError-shaped values are routed to it.
 */
export const getErrorMessageFromCaughtError = (t: (key: string) => string, error: Error | CombinedError): string =>
  'graphQLErrors' in error
    ? getFirstErrorMessageFromCombinedErrors(t, error)
    : error.message;

export const findGraphqlErrorWithCode = (errorObject: CombinedError, code: string) => {
  return errorObject.graphQLErrors.find((e) => {
    return e.extensions.code === code;
  });
};
